import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import type { CreateSocialHistoryBody } from '@/types/patient-chart-api';

/**
 * POST /api/Patients/CreateSocialHistory
 *
 * Wire body curl-verified by Andrew 2026-05-20. See `CreateSocialHistoryBody`
 * type doc for the Social-History-specific gotchas (most notably: nested
 * `code.category` Coding on Create vs nulled on Update, `dataAbsentReason:
 * "n/a"` on both, lowercase `updatedby` wire field).
 *
 * Cache: invalidates `['patient-social-history', patientId]`. SignalR
 * `UpdatePatientChat` "SocialHistory" event from PROD-827 Slice 3 also
 * fires for concurrent sessions; the dedup helper short-circuits the
 * SignalR echo of THIS user's own save.
 */

interface CreateSocialHistoryParams {
  patientId: string;
  body: CreateSocialHistoryBody;
}

export function useCreateSocialHistory() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateSocialHistoryParams>({
    mutationFn: ({ body }) =>
      post<string>('/api/Patients/CreateSocialHistory', body),
    onSuccess: (_data, { patientId }) => {
      invalidateIfNotInFlight(qc, ['patient-social-history', patientId]);
    },
  });
}
